import { ICellRendererParams, IHeaderParams } from "ag-grid-community";
import { Clock, Info } from "lucide-react";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Updater } from "use-immer";

import {
  ColorType,
  IgButton,
  IgChip,
  IgStatusCircle,
  IgTooltip,
  IgTypography,
  IgTypographyTooltip,
} from "@intelligems/ig-theme";
import {
  ConfigurationDto,
  ExperienceListDto,
  ExperienceStatus,
  PersonalizationStatusTextMap,
  ScheduleDto,
  TabEnumFe,
} from "@intelligems/ig-types";
import {
  officialCategoryName,
  officialStatusName,
} from "@intelligems/ig-utils";
import { useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { checkBooleanScope } from "@src/components/LimitAccess/check-scopes";
import { useHasLivePostPurchaseWithSameAudience } from "@src/components/LimitAccess/experiment-scope-hooks";
import { ScopedStartExperienceWithDisabled } from "@src/components/LimitAccess/ScopedStartExperienceWithDisabled";
import { ExperienceDurationTooltip } from "@src/features/experiments/components/ExperienceDurationTooltip";
import { TestTypeIcon } from "@src/features/experiments/components/TestTypeIcon";
import {
  isRolloutEnabled,
  onPriceTestAction,
} from "@src/features/experiments/utils/experienceActionUtils";
import { TargetingOverview } from "@src/features/sharedExperience/components/targeting/TargetingOverview";
import { useOnPersonalizationActionClick } from "@src/features/sharedExperience/tableUtils";
import { AiTestNameSparkle } from "@src/components/Dialogs/AiTestNameSparkle";
import { useDialogs } from "@src/hooks/dialog";
import { useIsExperienceMutating } from "@src/hooks/experiences";
import { EXPERIMENT_ROUTES, PERSONALIZATION_ROUTES } from "@src/routes/routes";
import { useDispatch } from "@src/store";
import { getDefaultHiddenEndTime } from "@src/store/helpers";
import { updateDashboardFilter } from "@src/store/slices/dashboardFilters";
import { FoundExperimentDetails } from "@src/types/experience";
import { mpExperienceGenericAction } from "@src/utils/mixpanel";

export const SharedTargetingColumn = <T extends ExperienceListDto>(
  props: ICellRendererParams<ExperienceListDto> & {
    onGeneralChipClick: (id: string) => void;
    columnExpanded: boolean;
    maxPreviewTargetingChips: number;
    data: T;
  }
) => {
  const onChipClickWrapper = () => {
    props.onGeneralChipClick(props.data.id);
  };
  return (
    <TargetingOverview
      audienceState={props.data.audience}
      experience={props.data}
      pageTargeting={props.data.experiencePageTargeting}
      chipColor={"default"}
      hideTooltip={true}
      groupLabelCategories={true}
      cutOffAt={props.columnExpanded ? props.maxPreviewTargetingChips : 2}
      separateExpressions={true}
      onGeneralChipClick={onChipClickWrapper}
      experienceProductTargeting={props.data.experienceProductTargeting}
    />
  );
};

export const SharedTargetingColumnHeader = (
  props: IHeaderParams & { columnExpanded: boolean; onExpandClick: () => void }
) => {
  const theme = useTheme();
  const { columnExpanded, onExpandClick } = props;
  return (
    <Grid
      container
      direction={"row"}
      alignItems={"center"}
      justifyContent={"space-between"}
    >
      <Grid>
        <IgTypography color={theme.palette.text.secondary}>
          Targeting
        </IgTypography>
      </Grid>
      <Grid>
        {/*<IgIconButton size={"small"} onClick={onExpandClick}>*/}
        {/*  {columnExpanded ? (*/}
        {/*    <ChevronsDownUp size={16} />*/}
        {/*  ) : (*/}
        {/*    <ChevronsUpDown size={16} />*/}
        {/*  )}*/}
        {/*</IgIconButton>*/}
      </Grid>
    </Grid>
  );
};

export const SharedTitleColumn = (
  props: ICellRendererParams<ExperienceListDto> & {
    orgConfigurations: ConfigurationDto[];
    setCurrentExperience?: Updater<ExperienceListDto | undefined>;
    storeCurrencyConfig: { code?: string; locale?: string };
    renderLocation: string;
  }
) => {
  const {
    data = {} as ExperienceListDto,
    orgConfigurations,
    setCurrentExperience,
    storeCurrencyConfig,
    renderLocation,
  } = props;
  const theme = useTheme();
  const dispatch = useDispatch();
  const { openDialog } = useDialogs();

  if (!data || !data.id) {
    console.warn("SharedTitleColumn: Received invalid data", { data });
    return null;
  }

  const experienceType =
    data.category === "personalization" ? "" : `&type=${data.type}`;
  const navigateLink = `${data.category === "personalization" ? PERSONALIZATION_ROUTES.Personalization : EXPERIMENT_ROUTES.Experiment}/${data.id}?action=edit${experienceType}&tab=${
    data.status === "pending" ? TabEnumFe.MODS : TabEnumFe.ANALYTICS
  }`;
  const isEdit = data.status === "pending" || data.status === "paused";
  const state = isEdit ? { id: data.id } : { name: "overview" };

  const renderRolloutOption =
    data.category === "experiment" &&
    (data.status === "started" || data.status === "paused") &&
    isRolloutEnabled(data) &&
    data.variations.some((variation) => variation.percentage === 100) &&
    checkBooleanScope("personalizations", orgConfigurations);

  return (
    <Grid
      container
      direction={"row"}
      alignItems={"center"}
      justifyContent={"flex-start"}
      columnSpacing={1}
    >
      {data.category === "experiment" && (
        <Grid>
          <Link
            style={{ textDecoration: "none", color: "black" }}
            to={navigateLink}
            state={state}
            onClick={() => {
              if (!isEdit) {
                dispatch(
                  updateDashboardFilter({
                    key: "experienceId",
                    value: undefined,
                  })
                );
              }
              mpExperienceGenericAction(data, "viewed", renderLocation);
            }}
          >
            <TestTypeIcon fontSize={"20px"} testFlowType={data.type} />
          </Link>
        </Grid>
      )}
      <Grid size="grow">
        <Link
          style={{
            textDecoration: "none",
            fontWeight: 400,
          }}
          to={navigateLink}
          state={state}
          onClick={() => {
            if (!isEdit) {
              dispatch(
                updateDashboardFilter({
                  key: "experienceId",
                  value: undefined,
                })
              );
            }

            mpExperienceGenericAction(data, "viewed", renderLocation);
          }}
        >
          <IgTypographyTooltip
            variant={"body1"}
            textMaxWidth={"240px"}
            text={data.name}
            textSx={{ fontWeight: 400 }}
          />
        </Link>
      </Grid>
      <Grid>
        <AiTestNameSparkle experienceId={data.id} experienceName={data.name} />
      </Grid>
      {renderRolloutOption ? (
        <Grid>
          <IgTooltip
            title={
              <IgTypography sx={{ display: "block" }}>
                <IgTypography fontWeight={600}>
                  Trying to Personalize?
                </IgTypography>
                <div>&nbsp;</div>
                This test is sending 100% of traffic to one group. It’s better
                to roll it out as a Personalization. Personalizations support
                the same changes, targeting, and analytics without the need for
                test groups.
                <div>&nbsp;</div>
                <IgTypography
                  onClick={() => {
                    if (setCurrentExperience) {
                      setCurrentExperience(data);
                    }
                    if (data.testTypes.hasTestPricing) {
                      onPriceTestAction(
                        data,
                        "end",
                        storeCurrencyConfig?.code ?? "USD",
                        dispatch
                      );
                    } else {
                      dispatch(
                        updateDashboardFilter({
                          key: "hiddenEndTime",
                          value: getDefaultHiddenEndTime(data),
                        })
                      );
                      openDialog("rollouts");
                    }
                  }}
                  sx={{
                    textDecoration: "underline",
                    "&:hover": {
                      cursor: "pointer",
                    },
                  }}
                >
                  Roll out to a Personalization
                </IgTypography>
              </IgTypography>
            }
          >
            <Info
              size={16}
              color={theme.igIconColors.warning}
              style={{
                cursor: "pointer",
              }}
            />
          </IgTooltip>
        </Grid>
      ) : null}
    </Grid>
  );
};

export const SharedStatusColumn = (
  params: ICellRendererParams<ExperienceListDto> & { schedules: ScheduleDto[] }
) => {
  const { data = {} as ExperienceListDto, schedules, value } = params;
  const org = useGetOrg();
  const statusTypeColorMap: Record<ExperienceStatus | "archived", ColorType> = {
    ended: "secondary",
    pending: "secondary",
    paused: "secondary",
    started: "success",
    archived: "primary",
  };

  const filteredSchedules = useMemo(() => {
    return schedules.filter((schedule) => schedule.experienceId === data.id);
  }, [schedules, data.id]);

  if (filteredSchedules.length > 0) {
    return (
      <IgTooltip
        title={
          <ExperienceDurationTooltip
            experience={data}
            organization={org.data.org}
            schedules={filteredSchedules}
          />
        }
      >
        <IgChip
          size={"small"}
          sx={{
            backgroundColor: `transparent !important`,
            border: "none",
          }}
          label={
            <Grid
              container
              columnSpacing={1}
              alignItems={"center"}
              flexWrap={"nowrap"}
            >
              <Grid sx={{ display: "flex", alignItems: "center" }}>
                {filteredSchedules.length > 0 ? (
                  <Clock size={14} />
                ) : (
                  <IgStatusCircle
                    color={
                      statusTypeColorMap[data.archivedAtTs ? "archived" : value]
                    }
                  />
                )}
              </Grid>
              <Grid>
                <IgTypography>
                  {data.status === "pending" || data.status === "paused"
                    ? "Scheduled"
                    : `${officialStatusName(data.status)} in Schedule`}
                </IgTypography>
              </Grid>
            </Grid>
          }
        />
      </IgTooltip>
    );
  }

  return (
    <IgTooltip
      title={
        <ExperienceDurationTooltip
          experience={data}
          organization={org.data.org}
          schedules={filteredSchedules}
        />
      }
    >
      <IgChip
        size={"small"}
        sx={{
          backgroundColor: `transparent !important`,
          border: "none",
        }}
        label={
          <Grid
            container
            columnSpacing={1}
            alignItems={"center"}
            flexWrap={"nowrap"}
          >
            <Grid>
              <IgStatusCircle
                color={
                  statusTypeColorMap[data.archivedAtTs ? "archived" : value]
                }
              />
            </Grid>
            <Grid>
              <IgTypography>
                {
                  PersonalizationStatusTextMap[
                    data.archivedAtTs ? "archived" : value
                  ]
                }
              </IgTypography>
            </Grid>
          </Grid>
        }
      />
    </IgTooltip>
  );
};

// TODO - CP need to move this to the more menu
export const SharedActionColumn = (
  params: ICellRendererParams<ExperienceListDto> & {
    hasPriceModAccess: boolean;
    hasOfferModAccess: boolean;
    hasCheckoutBlockModAccess: boolean;
    inactiveSubscriptionGate: boolean;
    isUnderOfferLimit: boolean;
    isAdmin: boolean;
    onActionClick: (personalization: ExperienceListDto) => void;
    getIsUsedInTest: (experienceId: string) => FoundExperimentDetails;
  }
) => {
  const {
    data = {} as ExperienceListDto,
    inactiveSubscriptionGate,
    hasOfferModAccess,
    hasPriceModAccess,
    hasCheckoutBlockModAccess,
    isUnderOfferLimit,
    isAdmin,
    onActionClick,
    getIsUsedInTest,
  } = params;
  // AG Grid reuses this renderer's component instance as rows scroll and as row
  // data updates in place, so the archived early return below must sit after
  // every hook call. Interleaving it changed the hook count between renders on
  // the same fiber and threw "Rendered fewer hooks than expected" (React #300).
  const isStartExperienceMutating = useIsExperienceMutating(
    "startExperience",
    data.id
  );
  const isPauseExperienceMutating = useIsExperienceMutating(
    "pauseExperience",
    data.id
  );
  const onPersonalizationActionClick = useOnPersonalizationActionClick();
  const hasLivePostPurchaseWithSameAudience =
    useHasLivePostPurchaseWithSameAudience(data);
  const isPostPurchaseCheckoutEnabled = useConfiguration(
    "enable_post_purchase_checkout"
  )?.value;

  const isUsedInTest = getIsUsedInTest(data.id);

  if (data.archivedAtTs) {
    return "";
  }

  const shouldBlockPriceStart =
    data.testTypes.hasTestPricing && !hasPriceModAccess;
  const shouldBlockOfferStart =
    data.testTypes.hasTestCampaign && !hasOfferModAccess;
  const shouldBlockCheckoutBlockStart =
    data.testTypes.hasTestCheckoutBlocks && !hasCheckoutBlockModAccess;
  const subscriptionPlanGate =
    !isAdmin &&
    (shouldBlockOfferStart ||
      shouldBlockPriceStart ||
      shouldBlockCheckoutBlockStart);

  return (
    <ScopedStartExperienceWithDisabled
      experience={data}
      debugId={"site-offer-table-action-button"}
    >
      <IgTooltip
        title={
          (data.status === "pending" || data.status === "paused") &&
          inactiveSubscriptionGate
            ? `Approve billing to start this ${officialCategoryName(data.category, true)}.`
            : (data.status === "pending" || data.status === "paused") &&
                subscriptionPlanGate
              ? `This ${officialCategoryName(data.category, true)} includes modifications not in your plan. Upgrade your plan to start this ${officialCategoryName(data.category, true)}.`
              : (data.status === "pending" || data.status === "paused") &&
                  !isUnderOfferLimit &&
                  data.testTypes.hasTestCampaign
                ? `Not enough function space for this ${officialCategoryName(data.category, true)}.`
                : ""
        }
      >
        <IgButton
          size={"extraSmall"}
          onClick={() => {
            onPersonalizationActionClick({
              personalization: data,
              schedules: data.schedules || [],
              isUsedInTest,
              onActionClick,
            });
          }}
          loading={
            isStartExperienceMutating > 0 || isPauseExperienceMutating > 0
          }
          disabled={
            isPauseExperienceMutating > 0 ||
            isStartExperienceMutating > 0 ||
            ((data.status === "pending" || data.status === "paused") &&
              inactiveSubscriptionGate) ||
            ((data.status === "pending" || data.status === "paused") &&
              subscriptionPlanGate) ||
            ((data.status === "pending" || data.status === "paused") &&
              !isUnderOfferLimit &&
              data.testTypes.hasTestCampaign) ||
            (data.testTypes.hasTestPostPurchase &&
              data.status !== "started" &&
              hasLivePostPurchaseWithSameAudience &&
              !isAdmin) ||
            (data.testTypes.hasTestPostPurchase &&
              data.status !== "started" &&
              !isPostPurchaseCheckoutEnabled &&
              !isAdmin)
          }
          variant={data.status === "started" ? "text" : "outlined"}
        >
          {data.status === "started" ? "Disable" : "Enable"}
        </IgButton>
      </IgTooltip>
    </ScopedStartExperienceWithDisabled>
  );
};
